import React, { useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, LinearProgress,
  MenuItem, Stack, Step, StepLabel, Stepper, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, Tooltip,
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../../contexts/AuthContext';
import { ROLE_DEFINITIONS, membersFromOrgDoc } from '../../../services/roleService';
import { VIEW_AS_ROLE_ORDER } from '../../../services/viewAs';
import { readRosterFile, mapColumnsWithAi, createAccountsFromRoster } from '../../../services/rosterImportService';
import { buildRows, resolveRow, detectDuplicates, rowStatus, FLAG_LABELS, ROSTER_TEMPLATE_CSV } from '../utils/rosterImport';
import { unitOptions } from './members/memberForm';

const STEPS = ['Upload', 'Map columns', 'Verify & correct', 'Create accounts'];
const FIELDS = [
  ['name', 'Full name'], ['firstName', 'First name'], ['lastName', 'Last name'], ['email', 'Email'], ['title', 'Title'],
  ['site', 'Site'], ['department', 'Department'], ['nwt', 'NWT'], ['role', 'Access level'],
];
const UNIT_FLAGS = ['unknown_site', 'ambiguous_site', 'unknown_department', 'ambiguous_department', 'unknown_team', 'ambiguous_team', 'missing_team', 'missing_department', 'missing_site', 'missing_email', 'bad_email'];

/**
 * Phase 2 — member roster import (Beta-1 plan P4-16, Tim D07.1). Upload any HR export, map the
 * columns (AI when WF7b is configured, else synonyms), verify rows side by side against the
 * confirmed structure (unknown department, ambiguous team, missing email flagged and fixable in
 * place), optionally scope to one site (Bayview pilot pattern), then create accounts through
 * `hugoCreateOrgUser` in batches with Notify / Quiet. Re-importing the same file skips rows that
 * are already members.
 */
export default function RosterImportDialog({ open, onClose, organization, onDone }) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [parsed, setParsed] = useState(null); // { headers, rows, fileName }
  const [mapping, setMapping] = useState({});
  const [mappingSource, setMappingSource] = useState('local');
  const [edits, setEdits] = useState({}); // line → patch
  const [scopeSiteId, setScopeSiteId] = useState('');
  const [notify, setNotify] = useState(true);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const opts = useMemo(() => unitOptions(organization), [organization]);
  const members = useMemo(() => membersFromOrgDoc(organization), [organization]);

  const reset = () => { setStep(0); setError(''); setParsed(null); setMapping({}); setEdits({}); setScopeSiteId(''); setProgress(null); setResults(null); };
  const close = () => { reset(); onClose(); };

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    try {
      const { headers, rows } = await readRosterFile(file);
      if (!headers.length || !rows.length) throw new Error('The file has no rows.');
      const { mapping: m, source } = await mapColumnsWithAi(headers, rows);
      setParsed({ headers, rows, fileName: file.name });
      setMapping(m);
      setMappingSource(source);
      setStep(1);
    } catch (err) {
      setError(err.message || 'Could not read the file.');
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([ROSTER_TEMPLATE_CSV], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'repsit-roster-template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Verify step: resolve every row, apply inline edits, mark duplicates.
  const rows = useMemo(() => {
    if (!parsed) return [];
    const departments = organization?.departments || [];
    const teams = organization?.teams || [];
    const people = buildRows(parsed.headers, parsed.rows, mapping);
    const resolved = people.map((p) => {
      const patch = edits[p.line] || {};
      const person = { ...p, ...(patch.email != null ? { email: patch.email.toLowerCase() } : {}) };
      const r = resolveRow(person, organization, { scopeSiteId: scopeSiteId || null });
      const fixed = { ...r };
      if (patch.role) fixed.role = patch.role;
      if (patch.nwtId) {
        fixed.nwtId = patch.nwtId;
        fixed.deptId = teams.find((x) => x.id === patch.nwtId)?.departmentId || fixed.deptId;
        fixed.siteId = departments.find((d) => d.id === fixed.deptId)?.siteId || fixed.siteId;
      }
      if (patch.deptId) { fixed.deptId = patch.deptId; fixed.siteId = departments.find((d) => d.id === patch.deptId)?.siteId || fixed.siteId; }
      if (patch.siteId) fixed.siteId = patch.siteId;
      // recompute the unit / email flags after fixes
      fixed.flags = r.flags.filter((f) => !UNIT_FLAGS.includes(f));
      if ((fixed.role === 'team_lead' || fixed.role === 'respondent') && !fixed.nwtId) fixed.flags.push(r.flags.includes('ambiguous_team') ? 'ambiguous_team' : r.flags.includes('unknown_team') ? 'unknown_team' : 'missing_team');
      if (fixed.role === 'department_lead' && !fixed.deptId) fixed.flags.push(r.flags.includes('ambiguous_department') ? 'ambiguous_department' : r.flags.includes('unknown_department') ? 'unknown_department' : 'missing_department');
      if (fixed.role === 'site_manager' && !fixed.siteId) fixed.flags.push('missing_site');
      if (!fixed.email) fixed.flags.push('missing_email');
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fixed.email)) fixed.flags.push('bad_email');
      return fixed;
    });
    return detectDuplicates(resolved, members);
  }, [parsed, mapping, edits, organization, scopeSiteId, members]);

  const counts = useMemo(() => rows.reduce((a, r) => { a[rowStatus(r)] += 1; return a; }, { ready: 0, fix: 0, skip: 0 }), [rows]);
  const readyRows = rows.filter((r) => rowStatus(r) === 'ready');
  const setEdit = (line, patch) => setEdits((e) => ({ ...e, [line]: { ...(e[line] || {}), ...patch } }));

  const run = async () => {
    setStep(3);
    setProgress({ done: 0, total: readyRows.length });
    const res = await createAccountsFromRoster({ organizationId: organization.id, rows: readyRows, notify, user, onProgress: (p) => setProgress({ done: p.done, total: p.total }) });
    setResults(res);
    if (onDone) onDone(res);
  };

  const statusChip = (r) => {
    const s = rowStatus(r);
    return <Chip size="small" label={s === 'ready' ? 'Ready' : s === 'fix' ? 'Fix' : 'Skip'} color={s === 'ready' ? 'success' : s === 'fix' ? 'warning' : 'default'} variant={s === 'skip' ? 'outlined' : 'filled'} />;
  };
  const created = results ? results.filter((r) => r.result?.isNewUser).length : 0;
  const linked = results ? results.filter((r) => r.result && !r.result.isNewUser).length : 0;
  const failed = results ? results.filter((r) => r.error).length : 0;

  return (
    <Dialog open={open} onClose={step === 3 && !results ? undefined : close} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GroupAddIcon color="primary" /> Import members
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 2 }}>{STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}</Stepper>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {step === 0 && (
          <Box>
            <Box
              sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
            >
              <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" gutterBottom>Upload your people list</Typography>
              <Typography variant="body2" color="text.secondary">
                Any HR export works — CSV or Excel with names, emails, titles and where people sit (site, department, Natural Work Team). REPSit maps the columns to your confirmed structure; you verify every row before any account is created.
              </Typography>
              <input ref={fileRef} type="file" hidden accept=".csv,.xlsx,.xls,.txt" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="roster-file" />
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
              <Button size="small" startIcon={<DownloadIcon />} onClick={downloadTemplate}>Download the template (CSV)</Button>
              <Typography variant="caption" color="text.secondary">Columns: First Name · Last Name · Email · Title · Site · Department · NWT · Access Level</Typography>
            </Stack>
          </Box>
        )}

        {step === 1 && parsed && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {parsed.fileName}: {parsed.rows.length} rows, {parsed.headers.length} columns. Columns were matched {mappingSource === 'ai' ? 'by the AI mapper' : 'by name'} — adjust anything that is wrong.
            </Alert>
            <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow><TableCell>REPSit field</TableCell><TableCell>Column in your file</TableCell><TableCell>Sample</TableCell></TableRow></TableHead>
              <TableBody>
                {FIELDS.map(([field, label]) => (
                  <TableRow key={field}>
                    <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                    <TableCell>
                      <TextField select size="small" value={mapping[field] ?? ''} onChange={(e) => setMapping((m) => ({ ...m, [field]: e.target.value === '' ? undefined : Number(e.target.value) }))} sx={{ minWidth: 220 }}>
                        <MenuItem value="">— not in this file —</MenuItem>
                        {parsed.headers.map((h, i) => <MenuItem key={i} value={i}>{h || `(column ${i + 1})`}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{mapping[field] != null ? parsed.rows[0]?.[mapping[field]] : ''}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
            {mapping.email == null && <Alert severity="warning" sx={{ mt: 2 }}>No email column — logins cannot be created without an email. You can type emails in the next step, or add a column to the file.</Alert>}
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 1.5 }}>
              <Stack direction="row" spacing={1}>
                <Chip label={`${counts.ready} ready`} color="success" />
                <Chip label={`${counts.fix} to fix`} color="warning" />
                <Chip label={`${counts.skip} skipped`} variant="outlined" />
              </Stack>
              <Box sx={{ flex: 1 }} />
              <TextField select size="small" label="Only this site" value={scopeSiteId} onChange={(e) => setScopeSiteId(e.target.value)} sx={{ minWidth: 240 }} helperText="Pilot one facility first, if you like">
                <MenuItem value="">All sites in the file</MenuItem>
                {opts.sites.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
              </TextField>
            </Stack>
            <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell><TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Title</TableCell>
                    <TableCell>Access level</TableCell><TableCell>Site · Department · NWT</TableCell><TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const st = rowStatus(r);
                    const needsTeam = ['team_lead', 'respondent'].includes(r.role);
                    const teamChoices = r.candidates?.team?.length ? r.candidates.team.map((t) => opts.teams.find((o) => o.id === t.id)).filter(Boolean) : opts.teams;
                    return (
                      <TableRow key={r.line} sx={{ bgcolor: st === 'fix' ? 'rgba(255,152,0,0.06)' : st === 'skip' ? 'action.hover' : undefined }}>
                        <TableCell><Typography variant="caption">{r.line}</Typography></TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600}>{r.displayName || '—'}</Typography><Typography variant="caption" color="text.secondary">{[r.siteName, r.deptName, r.nwtName].filter(Boolean).join(' · ')}</Typography></TableCell>
                        <TableCell>
                          {r.flags.includes('missing_email') || r.flags.includes('bad_email') ? (
                            <TextField size="small" placeholder="email" value={edits[r.line]?.email ?? r.email} onChange={(e) => setEdit(r.line, { email: e.target.value })} error sx={{ minWidth: 200 }} inputProps={{ 'aria-label': `Email for ${r.displayName}` }} />
                          ) : r.email}
                        </TableCell>
                        <TableCell><Typography variant="caption">{r.title}</Typography></TableCell>
                        <TableCell>
                          <TextField select size="small" value={r.role} onChange={(e) => setEdit(r.line, { role: e.target.value })} sx={{ minWidth: 200 }} disabled={st === 'skip'}>
                            {VIEW_AS_ROLE_ORDER.map((k) => <MenuItem key={k} value={k}>{ROLE_DEFINITIONS[k]?.label || k}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          {needsTeam ? (
                            <TextField select size="small" value={r.nwtId || ''} onChange={(e) => setEdit(r.line, { nwtId: e.target.value })} sx={{ minWidth: 260 }} disabled={st === 'skip'} error={!r.nwtId}>
                              <MenuItem value="">— pick an NWT —</MenuItem>
                              {teamChoices.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                            </TextField>
                          ) : r.role === 'department_lead' ? (
                            <TextField select size="small" value={r.deptId || ''} onChange={(e) => setEdit(r.line, { deptId: e.target.value })} sx={{ minWidth: 260 }} disabled={st === 'skip'} error={!r.deptId}>
                              <MenuItem value="">— pick a department —</MenuItem>
                              {opts.departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}
                            </TextField>
                          ) : r.role === 'site_manager' ? (
                            <TextField select size="small" value={r.siteId || ''} onChange={(e) => setEdit(r.line, { siteId: e.target.value })} sx={{ minWidth: 200 }} disabled={st === 'skip'} error={!r.siteId}>
                              <MenuItem value="">— pick a site —</MenuItem>
                              {opts.sites.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                            </TextField>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Organization-wide</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5} alignItems="flex-start">
                            {statusChip(r)}
                            {r.flags.map((f) => <Typography key={f} variant="caption" color={['already_member', 'duplicate_in_file', 'outside_scope'].includes(f) ? 'text.secondary' : 'warning.main'}>{FLAG_LABELS[f] || f}</Typography>)}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <FormControlLabel
              sx={{ mt: 2, alignItems: 'flex-start' }}
              control={<Switch checked={notify} onChange={(e) => setNotify(e.target.checked)} />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>{notify ? 'Notify: send each new member the welcome email' : 'Quiet: create accounts without emailing'}</Typography>
                  <Typography variant="caption" color="text.secondary">Welcome email is notification-only — nothing for the member to acknowledge. Recommended: the CEO sends a heads-up email first, then import with Notify on.</Typography>
                </Box>
              }
            />
          </Box>
        )}

        {step === 3 && (
          <Box sx={{ py: 2 }}>
            {!results ? (
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="h6">Creating accounts… {progress?.done || 0} / {progress?.total || 0}</Typography>
                <LinearProgress variant="determinate" value={progress?.total ? (progress.done / progress.total) * 100 : 0} sx={{ mt: 2, maxWidth: 420, mx: 'auto' }} />
              </Box>
            ) : (
              <Box>
                <Alert severity={failed ? 'warning' : 'success'} icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                  {created} account{created === 1 ? '' : 's'} created, {linked} existing login{linked === 1 ? '' : 's'} linked, {failed} failed.{' '}
                  {notify ? 'Welcome emails are on their way.' : 'No emails were sent — hand over the temporary passwords below.'}
                </Alert>
                <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Result</TableCell>{!notify && <TableCell>Temporary password</TableCell>}</TableRow></TableHead>
                    <TableBody>
                      {results.map(({ row, result, error: err }) => (
                        <TableRow key={row.line}>
                          <TableCell>{row.displayName}</TableCell>
                          <TableCell>{row.email}</TableCell>
                          <TableCell>{err ? <Typography variant="caption" color="error">{err}</Typography> : result?.isNewUser ? 'Created' : 'Linked (already had a login)'}</TableCell>
                          {!notify && <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{result?.tempPassword || '—'}</Typography></TableCell>}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 0 && <Button onClick={close}>Cancel</Button>}
        {step === 1 && (<><Button onClick={() => setStep(0)}>Back</Button><Button variant="contained" onClick={() => setStep(2)} disabled={mapping.name == null && mapping.firstName == null && mapping.lastName == null}>Next: verify</Button></>)}
        {step === 2 && (
          <>
            <Button onClick={() => setStep(1)}>Back</Button>
            <Tooltip title={counts.fix ? `${counts.fix} row(s) still need a fix — they will not be created` : ''}>
              <span>
                <Button variant="contained" onClick={run} disabled={readyRows.length === 0} startIcon={<GroupAddIcon />}>
                  Create {readyRows.length} account{readyRows.length === 1 ? '' : 's'} {notify ? 'and notify' : 'quietly'}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
        {step === 3 && results && <Button variant="contained" onClick={close}>Done</Button>}
      </DialogActions>
    </Dialog>
  );
}
